import React from 'react';
import { Trophy, Target, ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { CommissionReportButton } from './CommissionReportButton';
import { OfficerData } from '../data/officers';
// Salesforce commission report — replace with the real report URL
const COMMISSION_REPORT_URL = 'https://your-org.lightning.force.com/lightning/r/Report/00O000000000000/view';
interface RankStatusHeroProps {
  officer: OfficerData;
}
export function RankStatusHero({
  officer
}: RankStatusHeroProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6" data-id="element-544">
      <div className="bg-[#0a1e5e] px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-id="element-545">
        <div className="flex items-center gap-3 text-white min-w-0" data-id="element-546">
          <Trophy className="w-6 h-6 text-yellow-400 shrink-0" data-id="element-547" />
          <h2 className="text-lg font-bold truncate" data-id="element-548">Career Progression</h2>
        </div>
        <div className="text-blue-200 text-xs sm:text-sm font-medium" data-id="element-549">
          Your Path. Your Production. Your Future.
        </div>
      </div>

      <div className="p-4 sm:p-6 flex flex-col lg:flex-row gap-6 items-stretch" data-id="element-550">
        {/* Current Status */}
        <div className="flex-1 w-full bg-blue-50 rounded-xl p-5 sm:p-6 border border-blue-100 relative overflow-hidden min-w-0" data-id="element-551">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full -mr-10 -mt-10 opacity-50 pointer-events-none" data-id="element-552"></div>
          <div className="relative" data-id="element-553">
            <p className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-1" data-id="element-554">
              Current Rank
            </p>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-[#0a1e5e] mb-4 leading-tight break-words" data-id="element-555">
              {officer.currentRank}
            </h3>

            <CommissionReportButton reportUrl={COMMISSION_REPORT_URL} data-id="element-556" />
          </div>
        </div>

        <div className="hidden lg:flex items-center justify-center text-gray-300 shrink-0" data-id="element-557">
          <ArrowRight className="w-10 h-10" data-id="element-558" />
        </div>

        {/* Next Target */}
        <div className="flex-[2] w-full bg-white rounded-xl p-5 sm:p-6 border border-gray-200 shadow-sm min-w-0" data-id="element-559">
          <div className="mb-5" data-id="element-560">
            <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-561">
              Next Target
            </p>
            <h3 className="text-xl sm:text-2xl font-extrabold text-[#0a1e5e] flex items-center gap-2 leading-tight" data-id="element-562">
              <span className="break-words" data-id="element-563">{officer.nextRank}</span>
              <Target className="w-5 h-5 text-blue-600 shrink-0" data-id="element-564" />
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-5 md:gap-6 relative" data-id="element-565">
            {/* OR badge - placed between grid columns on md+ */}
            <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 text-[10px] font-bold px-2 py-0.5 rounded-full text-gray-500 shadow-sm pointer-events-none" data-id="element-566">
              OR
            </div>

            {/* Path 1: Tenure */}
            <div className="min-w-0" data-id="element-567">
              <div className="flex justify-between gap-2 text-sm mb-2" data-id="element-568">
                <span className="font-semibold text-gray-700" data-id="element-569">
                  Path 1: Tenure
                </span>
                <span className="text-gray-500 shrink-0 text-xs sm:text-sm" data-id="element-570">
                  {officer.tenureProgress}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1.5 overflow-hidden" data-id="element-571">
                <div className="bg-blue-600 h-2.5 rounded-full" style={{
                width: `${officer.tenurePercent}%`
              }} data-id="element-572"></div>
              </div>
              <p className="text-xs text-gray-500" data-id="element-573">
                {officer.tenurePercent >= 100 ? 'Tenure requirement met' : 'Toward tenure requirement'}
              </p>
            </div>

            {/* Path 2: Units */}
            <div className="min-w-0" data-id="element-574">
              <div className="flex justify-between gap-2 text-sm mb-2 flex-wrap" data-id="element-575">
                <span className="font-semibold text-[#0a1e5e] flex items-center gap-1.5 flex-wrap" data-id="element-576">
                  Path 2: Units
                  <span className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5 rounded uppercase font-bold whitespace-nowrap" data-id="element-577">
                    Closer
                  </span>
                </span>
                <span className="text-[#0a1e5e] font-bold shrink-0 text-xs sm:text-sm" data-id="element-578">
                  {officer.unitsForNextRank} / {officer.unitsNeeded} units
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1.5 overflow-hidden" data-id="element-579">
                <div className="bg-green-500 h-2.5 rounded-full" style={{
                width: `${officer.unitsPercent}%`
              }} data-id="element-580"></div>
              </div>
              <p className="text-xs text-gray-600 font-medium" data-id="element-581">
                In any 4-month rolling period
              </p>
            </div>
          </div>

          <div className="mt-5 bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-3" data-id="element-582">
            <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" data-id="element-583" />
            <div className="min-w-0" data-id="element-584">
              <p className="text-sm font-semibold text-[#0a1e5e]" data-id="element-585">
                Action Plan
              </p>
              <p className="text-sm text-gray-700 leading-snug" data-id="element-586">
                {officer.actionPlan}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}